import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { ActionsService } from '@core/actions-modals/actions.service';
import { AbsenceTypesEnum } from '@features/absence/absence-types.enum';
import { requestStatuses } from '@features/absence/overview/requests-list/statuses.enum';
import { AuthService } from '@features/auth/auth.service';
import { UserRoles } from '@features/auth/user-roles';
import { MarketplaceService } from '@features/marketplace/marketplace.service';
import { Permissions } from '@features/qualifications/permissions';
import { SettingsService } from '@features/settings/settings.service';
import { SegmentService } from '@services/common/segment.service';
import { TimeService } from '@services/common/time.service';
import { IntegrationIds } from '@shared/interfaces/integrationIds.enum';
import { ToasterService } from '@ui-kit/app-toast/toaster.service';
import { handleError } from '@ui-kit/utility/handleHttpErrorResponse';
import { pluralize } from '@ui-kit/utility/pluralize';
import { CrudService } from 'collection';
import { GetEntityModel } from 'collection';
import { Collection, CollectionModel, Item, ItemModel } from 'collection';
import { differenceInMinutes, format, isBefore, parse } from 'date-fns';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { legacyParse, convertTokens } from '@date-fns/upgrade/v2';

marker('Created');

@Component({
  selector: 'add-absence-modal',
  templateUrl: './add-absence-modal.component.html',
  styleUrls: ['./add-absence-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddAbsenceModalComponent implements OnInit, OnDestroy {
  employeesCollection: Collection;
  absenceTypeCollection: Collection;
  absenceItem: ItemModel;
  workingDaysRequest: GetEntityModel = this._crud.createGetEntity({ name: 'working-days' });
  collection: CollectionModel;
  employeeModel: any;
  reasonModel: any | null;
  absenceTypeModel: any;

  absenceTypesEnum = AbsenceTypesEnum;
  requestStatuses = requestStatuses;

  ABSENCE_REASON_OPTIONS_LIST = [
    {
      name: this.absenceTypesEnum.Vacation,
      value: 1
    },
    {
      name: this.absenceTypesEnum.Absence,
      value: 0
    }
  ];

  leftDaysInfo: object;

  form = new UntypedFormGroup({});

  isVacation: boolean = false;
  editMode: boolean = false;

  selectedDays: number = 1;
  selectedTimePeriod: any;

  isAdmin: boolean = false;

  private _destroyed$ = new Subject();

  changeReason({ model }) {
    this.isVacation = model.name === this.absenceTypesEnum.Vacation;
    this.form.controls['is_vacation'].setValue(this.isVacation ? 1 : 0);
    this.getPeriodDays();
    this.changeFormValidators();
  }

  isSameDate() {
    let from = this.form.controls['startDate'].value;
    let to = this.form.controls['endDate'].value;

    from = format(legacyParse(from), convertTokens('DD.MM.YYYY'));
    to = format(legacyParse(to), convertTokens('DD.MM.YYYY'));

    return from === to;
  }

  getDaysLeft(user) {
    this.leftDaysInfo = {
      daysLeftNumber: user?.vacation_days_amount,
      dayLabel: pluralize(user?.vacation_days_amount, 'day'),
      leftInLabel: marker('left in'),
      year: new Date().getFullYear()
    };
  }

  handleSelect({ event, model }): void {
    event.preventDefault();
    event.stopPropagation();

    if (model) {
      this.employeeModel = model;
      this.getDaysLeft(model);
    }
  }

  changeFrom(event) {
    if (event) {
      const from = this.form.controls['startDate'];
      const to = this.form.controls['endDate'];
      const isDateBefore = isBefore(legacyParse(to.value), legacyParse(from.value));

      if (isDateBefore) {
        to.setValue(from.value);
      }

      this.getPeriodDays();
      this.updateFromToTimeValidators();
    }
  }

  changeTo(event) {
    if (event) {
      const from = this.form.controls['startDate'];
      const to = this.form.controls['endDate'];
      const isDateBefore = isBefore(legacyParse(to.value), legacyParse(from.value));

      if (isDateBefore) {
        from.setValue(to.value);
      }

      this.getPeriodDays();
      this.updateFromToTimeValidators();
    }
  }

  getPeriodDays(): void {
    const from = this.form.controls['startDate'].value;
    const to = this.form.controls['endDate'].value;

    if (from && to) {
      const query = {
        from,
        to,
        is_absence: this.isVacation ? 0 : 1
      };

      this.workingDaysRequest.get({ query }).subscribe((res) => {
        this.selectedDays = res.data.days;
        this._cds.markForCheck();
      });
    }
  }

  save(): void {
    if (this.form.valid) {
      const data = this.form.value;

      data.startTime = format(legacyParse(data.startTime), 'HH:mm:ss');
      data.endTime = format(legacyParse(data.endTime), 'HH:mm:ss');

      switch (this.editMode) {
        case true:
          this.update(data);
          break;
        case false:
          this.create(data);
          break;
      }
    } else {
      this.form.markAllAsTouched();
    }
  }

  create(data): void {
    const postData = this.mapPostData(data);

    this.absenceItem.createItem({ data: postData }).subscribe(
      (res) => {
        this.initSegmentEvent();

        this.data.subject.next({
          action: 'create',
          item: res.data
        });
        this.close();
        this._toastService.show({
          type: 'success',
          message: marker('Absence have successfully created')
        });
      },
      (err) => {
        const errorResponse = err.error;
        handleError(errorResponse, this._toastService);
      }
    );
  }

  private initSegmentEvent(): void {
    if (this.isVacation) {
      this._segmentService.track('Vacation Create', {
        vacation_create: true
      });
    } else {
      this._segmentService.track('Absence Create', {
        absence_create: true
      });
    }
  }

  update(data): void {
    const postData = this.mapPostData(data);

    this.absenceItem.updateItem({ data: postData }).subscribe(
      (res) => {
        this.data.subject.next({
          action: 'update',
          item: res.data
        });
        this.close();
        this._toastService.show({
          type: 'success',
          message: marker('Absence have successfully updated!')
        });
      },
      (err) => {
        const errorResponse = err.error;
        handleError(errorResponse, this._toastService);
      }
    );
  }

  canUpdate(): boolean {
    const item = this.absenceItem.data;
    const currentRole = this._auth.currentRole;

    if (currentRole === UserRoles.Admin) {
      return this._auth.can(Permissions.update);
    }
  }

  delete(): void {
    this.data.subject.next({
      action: 'delete',
      item: this.absenceItem.data
    });

    this.close();
  }

  close(): void {
    this._dialogRef.close();
  }

  getCurrentUser() {
    const currentUser = { ...this._auth.currentUser };
    currentUser['full_name'] = `${currentUser['first_name'] || ''} ${currentUser['last_name'] || ''}`;
    return currentUser;
  }

  setEmployeeModel(user) {
    this.employeeModel = user;
    this.employeeModel['full_name'] = `${user['first_name'] || ''} ${user['last_name'] || ''}`;
  }

  updateDateTime(startDate, endDate, startTime, endTime, workingHours?) {
    const workingHoursSplit = workingHours?.split(':');
    const timeFrom = startTime.split(':');
    const timeTo = endTime.split(':');

    const timeToSet = {
      fromHours: timeFrom[0],
      fromMinutes: timeFrom[1],
      toHours: workingHours ? +timeTo[0] + +workingHoursSplit[0] : timeTo[0],
      toMinutes: workingHours ? +timeTo[1] + +workingHoursSplit[1] : timeTo[1]
    };

    const dateFrom = new Date(new Date(startDate).setHours(timeToSet.fromHours, timeToSet.fromMinutes));
    const dateTo = new Date(new Date(endDate).setHours(timeToSet.toHours, timeToSet.toMinutes));

    return { dateFrom, dateTo };
  }

  private initCreateForm() {
    this.reasonModel = this.ABSENCE_REASON_OPTIONS_LIST[1];

    const startDate = this.data.item ? this.data.item.startDate : new Date();
    const endDate = this.data.item ? this.data.item.endDate : new Date();
    const currentUser = this.data.item?.worker ? this.data.item.worker : this.getCurrentUser();

    this.form = new UntypedFormGroup(
      {
        is_vacation: new UntypedFormControl(0, Validators.required),
        startDate: new UntypedFormControl(format(legacyParse(startDate), convertTokens('YYYY-MM-DD')), [Validators.required]),
        endDate: new UntypedFormControl(format(legacyParse(endDate), convertTokens('YYYY-MM-DD')), Validators.required),
        startTime: new UntypedFormControl(null, Validators.required),
        endTime: new UntypedFormControl(null, Validators.required),
        absenceCategory: new UntypedFormControl('', Validators.required),
        userId: new UntypedFormControl(currentUser.id, Validators.required),
        comment: new UntypedFormControl('', Validators.maxLength(255))
      },
      [this.groupValidator]
    );

    this.employeeModel = currentUser;

    this.autofillTime();
    this.getDaysLeft(this.employeeModel);
    this.getPeriodDays();
    this.changeFormValidators();
    this.initTimeDateCheck();
  }

  groupValidator(group: AbstractControl): ValidationErrors | null {
    const isVacationCtrl = group.get('is_vacation');
    const fromCtrl = group.get('startTime');
    const toCtrl = group.get('endTime');

    const fromDateCtrl = group.get('startDate');
    const toDateCtrl = group.get('endDate');

    const from = format(legacyParse(fromDateCtrl.value), convertTokens('DD.MM.YYYY'));
    const to = format(legacyParse(toDateCtrl.value), convertTokens('DD.MM.YYYY'));

    const isSameDay = from === to;

    const isVacation = isVacationCtrl.value === 1;

    if (!fromCtrl.value || !toCtrl.value || isVacation || !isSameDay) {
      return null;
    }

    return new Date(fromCtrl.value) > new Date(toCtrl.value) ? { message: 'From time should be less then To time.' } : null;
  }

  private changeFormValidators(): void {
    if (this.isVacation) {
      this.form.controls['startTime'].clearValidators();
      this.form.controls['endTime'].clearValidators();
      this.form.controls['absenceCategory'].clearValidators();
    } else {
      const isSameDay = this.isSameDate();

      this.form.controls['startTime'].setValidators([Validators.required]);
      this.form.controls['endTime'].setValidators(Validators.required);

      if (!isSameDay) {
        this.form.controls['startTime'].clearValidators();
        this.form.controls['endTime'].clearValidators();
      }

      this.form.controls['absenceCategory'].setValidators(Validators.required);
    }

    this.form.get('startTime').updateValueAndValidity();
    this.form.get('endTime').updateValueAndValidity();
    this.form.get('absenceCategory').updateValueAndValidity();
  }

  private updateFromToTimeValidators(): void | boolean {
    const isSameDay = this.isSameDate();

    if (this.isVacation) {
      return false;
    }

    if (!isSameDay) {
      this.form.controls['startTime'].clearValidators();
      this.form.controls['endTime'].clearValidators();
    } else {
      this.form.controls['startTime'].setValidators(Validators.required);
      this.form.controls['endTime'].setValidators(Validators.required);
      this.autofillTime();
    }
    this.form.get('startTime').updateValueAndValidity();
    this.form.get('endTime').updateValueAndValidity();
  }

  private initEditForm(data): void {
    this.isVacation = !!data.is_vacation;
    this.absenceTypeModel = data.absenceType;
    this.setEmployeeModel(data.user);
    this.reasonModel = this.ABSENCE_REASON_OPTIONS_LIST[this.isVacation ? 0 : 1];

    const startTime = parse(data.from, 'yyyy-MM-dd HH:mm:ss', new Date());
    const endTime = parse(data.to, 'yyyy-MM-dd HH:mm:ss', new Date());

    this.form = new UntypedFormGroup(
      {
        is_vacation: new UntypedFormControl({ value: data.is_vacation, disabled: true }, Validators.required),
        startDate: new UntypedFormControl(format(legacyParse(data.from), convertTokens('YYYY-MM-DD')), Validators.required),
        endDate: new UntypedFormControl(format(legacyParse(data.to), convertTokens('YYYY-MM-DD')), Validators.required),
        startTime: new UntypedFormControl(startTime, Validators.required),
        endTime: new UntypedFormControl(endTime, Validators.required),
        absenceCategory: new UntypedFormControl(data.absence_type_id, Validators.required),
        userId: new UntypedFormControl({ value: data.user_id, disabled: true }, Validators.required),
        comment: new UntypedFormControl(data.comment, Validators.maxLength(255))
      },
      [this.groupValidator]
    );

    if (!data.from && !data.to) {
      this.autofillTime();
    }

    this.getDaysLeft(data.user);
    this.getPeriodDays();
    this.changeFormValidators();

    this.initTimeDateCheck();
    this.calculateTimePeriod();

    this._cds.markForCheck();
  }

  private mapPostData(data) {
    let postData = {};
    if (!this.isVacation) {
      // tslint:disable-next-line:prefer-const
      let { startDate, endDate, startTime, endTime } = data;

      if (!this.isSameDate()) {
        startTime = '00:00';
        endTime = '00:00';
      }

      const updatedDates = this.updateDateTime(startDate, endDate, startTime, endTime);

      postData = {
        from: format(legacyParse(updatedDates.dateFrom), convertTokens('YYYY-MM-DD HH:mm:ss')),
        to: format(legacyParse(updatedDates.dateTo), convertTokens('YYYY-MM-DD HH:mm:ss')),
        user_id: data.userId,
        comment: data.comment,
        absence_type_id: data.absenceCategory,
        is_vacation: this.isVacation ? 1 : 0
      };
    } else {
      const updatedDates = this.updateDateTime(data.startDate, data.endDate, '00:00', '00:00');

      postData = {
        from: format(legacyParse(updatedDates.dateFrom), convertTokens('YYYY-MM-DD HH:mm:ss')),
        to: format(legacyParse(updatedDates.dateTo), convertTokens('YYYY-MM-DD HH:mm:ss')),
        user_id: data.userId,
        comment: data.comment,
        absence_type_id: data.absenceCategory,
        is_vacation: this.isVacation ? 1 : 0
      };
    }
    return postData;
  }

  private initForm() {
    const modalData = this.data;

    if (modalData.item && modalData.item.hasOwnProperty('id') && modalData.item.id) {
      this.editMode = true;
      this.absenceItem.setParams('id', modalData.item.id);
      this.absenceItem.load().subscribe(() => {
        this.initEditForm(this.absenceItem.data);
        if (!this.canUpdate() && this.absenceItem?.data?.status === requestStatuses.Approve) {
          this.form.disable();
        }
      });
    } else {
      this.initCreateForm();
    }
  }

  getEmployeesList({ search, page }): void {
    this.employeesCollection.setParams('page', page);
    search.length
      ? this.employeesCollection.setParams('filter_name', search)
      : this.employeesCollection.clearParams('filter_name');
    this.employeesCollection.search();
  }

  getAbsenceTypeList({ search, page }): void {
    this.absenceTypeCollection.setParams('page', page);
    search.length ? this.absenceTypeCollection.setParams('name', search) : this.absenceTypeCollection.clearParams('name');

    if (this.isHoursAccountV1Enabled) {
      this.absenceTypeCollection.setParams('include_ha', true);
    }
    this.absenceTypeCollection.search();
  }

  changeAbsenceType({ model }): void {
    this.absenceTypeModel = model;
  }

  private getFormControl(controlName: string): AbstractControl {
    return this.form.get(controlName);
  }

  private calculateTimePeriod(updateTimeInputs = false): void | boolean {
    let start = this.getFormControl('startTime').value;
    let end = this.getFormControl('endTime').value;

    if (updateTimeInputs) {
      const updatedDateTimes = this.updateDateTime(
        this.getFormControl('startDate').value,
        this.getFormControl('endDate').value,
        format(legacyParse(start), convertTokens('HH:mm:ss')),
        format(legacyParse(end), convertTokens('HH:mm:ss'))
      );
      start = updatedDateTimes.dateFrom;
      end = updatedDateTimes.dateTo;
      this.getFormControl('startTime').setValue(start, { emitEvent: false, onlySelf: true });
      this.getFormControl('endTime').setValue(end, { emitEvent: false, onlySelf: true });
    }

    if (!start || !end) {
      return false;
    }

    if (start && end) {
      let diff = differenceInMinutes(legacyParse(end), legacyParse(start));
      if (diff < 0) {
        diff = 0;
      }
      this.selectedTimePeriod = this._timeService.secondsToPeriod(diff * 60);
    }
  }

  private initTimeDateCheck(): void {
    this.getFormControl('startTime').valueChanges.subscribe(() => this.calculateTimePeriod());
    this.getFormControl('endTime').valueChanges.subscribe(() => this.calculateTimePeriod());
    this.getFormControl('startDate').valueChanges.subscribe(() => this.calculateTimePeriod(true));
    this.getFormControl('endDate').valueChanges.subscribe(() => this.calculateTimePeriod(true));
  }

  autofillTime(): void {
    const defaultStartTime = '08:00';
    const startDate = this.getFormControl('startDate')?.value;
    const endDate = this.getFormControl('endDate')?.value;
    const isDateEqual = startDate === endDate;
    const isAbsence = this.getFormControl('is_vacation')?.value === 0;

    this._settingsService.initAdditionalSettings.pipe(takeUntil(this._destroyed$)).subscribe((settings) => {
      const defaultWorkingHours = settings['default_working_hours'];
      const isWorkingHoursNotZero = defaultWorkingHours !== '00:00';

      if (isDateEqual && isWorkingHoursNotZero && isAbsence) {
        const updatedTime = this.updateDateTime(startDate, endDate, defaultStartTime, defaultStartTime, defaultWorkingHours);

        this.getFormControl('startTime').setValue(updatedTime.dateFrom, { emitEvent: false, onlySelf: true });
        this.getFormControl('endTime').setValue(updatedTime.dateTo, { emitEvent: false, onlySelf: true });
      }
    });
  }

  get isHoursAccountV1Enabled(): boolean {
    return this._integrationsService.hasIntegrationEnabled(IntegrationIds.hours_account);
  }

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy() {
    this.absenceItem.clear();
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  constructor(
    private _toastService: ToasterService,
    private _crud: CrudService,
    private _auth: AuthService,
    private _actionsService: ActionsService,
    private _cds: ChangeDetectorRef,
    private _integrationsService: MarketplaceService,
    private _timeService: TimeService,
    private _segmentService: SegmentService,
    public _dialogRef: MatDialogRef<any>,
    private _settingsService: SettingsService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.isAdmin = this._auth.isAdmin();
    this.absenceItem = new Item({
      api: this._crud.createEntity({ name: 'absence' }),
      params: {
        expand: 'absenceType,user'
      }
    });

    this.employeesCollection = new Collection({
      api: this._crud.createEntity({ name: 'worker' }),
      params: {
        status: 'active',
        expand: 'full_name'
      }
    });

    this.employeesCollection.loadByScrolling = true;

    this.absenceTypeCollection = new Collection({
      api: this._crud.createEntity({ name: 'absence-type' }),
      params: {
        sort: 'name'
      }
    });

    this.absenceTypeCollection.loadByScrolling = true;
    setTimeout(() => {
      document.getElementById('hideButton')?.click();
    }, 16);
  }
}
